// Dashboard MTD/YTD: gastos por categoría + comparativo invoicing.paid_total.
// Muestra ambas ventanas (MTD y YTD) lado-a-lado con el net (paid - expenses).

import { useDashboard } from '../hooks/useExpenses';
import type { RangeSummary } from '../types';

function fmtCurrency(s: string): string {
  const n = Number(s);
  if (Number.isNaN(n)) return s;
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

function RangeCard({ title, range }: { title: string; range: RangeSummary }) {
  const expenseTotal = Number(range.expense_total);
  const invoicingTotal = Number(range.invoicing_paid_total);
  const net = Number(range.net);
  const netPositive = net >= 0;

  return (
    <div className="border rounded-md p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h4 className="text-sm font-semibold">{title}</h4>
        <span className="text-xs text-neutral-500">
          {range.start} → {range.end}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-neutral-500">Ingresos cobrados</div>
          <div className="font-mono">{fmtCurrency(range.invoicing_paid_total)}</div>
        </div>
        <div>
          <div className="text-xs text-neutral-500">Gastos</div>
          <div className="font-mono">{fmtCurrency(range.expense_total)}</div>
        </div>
        <div>
          <div className="text-xs text-neutral-500">Neto</div>
          <div className={`font-mono ${netPositive ? 'text-green-700' : 'text-red-600'}`}>
            {fmtCurrency(range.net)}
          </div>
        </div>
      </div>

      <div className="text-xs text-neutral-600">
        ratio gasto/ingreso:{' '}
        {invoicingTotal > 0
          ? `${((expenseTotal / invoicingTotal) * 100).toFixed(1)}%`
          : '—'}
      </div>

      <div>
        <div className="text-xs text-neutral-500 mb-1">Por categoría</div>
        {range.by_category.length === 0 ? (
          <div className="text-xs text-neutral-400">Sin gastos en el período.</div>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {range.by_category.map((row) => (
                <tr key={row.category_id} className="border-b last:border-b-0">
                  <td className="py-1">{row.name}</td>
                  <td className="py-1 text-right text-neutral-500">{row.count}</td>
                  <td className="py-1 text-right font-mono">{fmtCurrency(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function ExpensesDashboard() {
  const { data, loading, error, refetch } = useDashboard();

  if (loading && !data) {
    return <div className="p-6 text-sm text-neutral-500">Cargando dashboard…</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-red-600">
        Error: {error} · <button onClick={refetch} className="underline">Reintentar</button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="text-xs text-neutral-500">As-of: {data.as_of}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RangeCard title="Mes a la fecha (MTD)" range={data.ranges.mtd} />
        <RangeCard title="Año a la fecha (YTD)" range={data.ranges.ytd} />
      </div>
    </div>
  );
}
